<template>
  <div class="gf_control-input" :style="inputStyling">
    <input
      ref="input"
      class="gf_input"
      :value="inputValue"
      type="number"
      :min="min"
      :max="max"
      lang="en"
      :placeholder="placeholder"
      :class="inputClass"
      :disabled="disabled"
      @keypress="onKeyPress"
      @keyup.prevent="onChange"
      @input.prevent="onChange"
      @blur.prevent="blur" />

    <div v-if="units.length === 1" class="gf_selection-select" :class="unitClass">
      <span class="select--text">{{ units[0].name }}</span>
    </div>
    <div v-else>
      <GfDropdown
        :dropdown-styling-option-item="{ background: '#494949 !important' }"
        :dropdown-styling="dropdownStyling"
        :dropdown-styling-option="dropdownStylingOption"
        f-type="simple"
        :options="units"
        :value="unitSelected"
        @selected="selectedUnit" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import GfDropdown from './GfDropdown.vue';

export default defineComponent({
  name: 'GfInputUnit',
  components: {
    GfDropdown,
  },
  props: {
    id: [String, Number],
    type: String,
    placeholder: String,
    value: [String, Number, Boolean, Function, Object],
    unit: String,
    inputStyling: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
    screens: {
      type: [Array, Object],
      default: () => [],
    },
    units: {
      type: [Array, Object],
      default: () => [
        {
          name: 'px',
          value: 'px',
        },
        {
          name: 'em',
          value: 'em',
        },
        {
          name: 'rem',
          value: 'rem',
        },
        {
          name: '%',
          value: '%',
        },
      ],
    },
    unitClass: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: -999999,
    },
    max: {
      type: Number,
      default: 999999,
    },
    decimal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['onChange', 'created', 'change', 'focus', 'invalid'],
  data() {
    return {
      val: '',
      unitSelected: 'px',
      dropdownStyling: {
        width: '57px',
        border: '1px solid #494949',
        'border-left': 'none',
        'border-top-left-radius': '0px',
        'border-bottom-left-radius': '0px',
      },
      dropdownStylingOption: {
        width: '80px',
        background: '#494949 !important',
      },
      inputClass: '',
      inputValue: '',
      oldInputValue: '',
      inValidValue: false,
    };
  },
  computed: {
    acceptedCharCode() {
      /* charCode
        45: "-",
        46: ".",
        48-57: "0-9",
      */
      const list = [45, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57];
      return list;
    },
  },
  watch: {
    value(newV, oldV) {
      if (newV === oldV) return;
      this.inputValue = newV;
      this.oldInputValue = newV;
    },
  },
  created() {
    this.inputValue = this.value;
    this.oldInputValue = this.value;
    if (this.units.length === 1) {
      this.unitSelected = this.units[0].value;
    } else if (this.unit) {
      this.unitSelected = this.unit;
    }
    this.$emit('created', this);
  },
  unmounted() {
    this.inputValue = '';
  },
  methods: {
    selectedUnit(unit) {
      this.unitSelected = unit.value;
      this.requiredValidate();
      this.$emit('change', {
        id: this.id,
        value: this.inputValue,
        unit: this.unitSelected,
      });
    },
    isNumeric(num) {
      return !isNaN(num);
    },
    onKeyPress(event) {
      const keyboardCharCode = event.charCode;
      if (!this.acceptedCharCode.includes(keyboardCharCode)) {
        event.preventDefault();
        return;
      }
    },
    validateInput(value, event) {
      if (value === '') {
        return;
      }
      if (this.isNumeric(value)) {
        if (this.decimal) {
          const splitValue = value.toString().split('.');
          let decimalPath = splitValue[1];
          if (decimalPath && decimalPath.length >= this.decimal) {
            decimalPath = decimalPath.slice(0, this.decimal);
            this.inputValue = parseFloat(`${splitValue[0]}.${decimalPath}`);
            event.target.value = this.inputValue;
          } else {
            this.inputValue = value;
          }
        } else {
          if (this.inputValue != value || event.key == '.') {
            this.inputValue = parseInt(value);
            if (Number.isInteger(this.inputValue) && this.inputValue >= this.min && this.inputValue < this.max) {
              this.inputClass = '';
              this.inValidValue = false;
              event.target.value = this.inputValue;
              event.target.type = 'text';
              event.target.setSelectionRange(this.max, this.max);
              event.target.type = 'number';
            } else {
              this.inputClass = 'error';
              this.inValidValue = true;
              setTimeout(() => {
                event.target.value = value;
                event.target.type = 'text';
                event.target.setSelectionRange(this.max, this.max);
                event.target.type = 'number';
              });
            }
          }
        }
      }
      if (
        this.inputValue === '' ||
        isNaN(this.inputValue) ||
        this.inputValue < this.min ||
        this.inputValue > this.max
      ) {
        this.inputClass = 'error';
        this.inValidValue = true;
      } else {
        this.inputClass = '';
        this.inValidValue = false;
      }
      if (!this.inValidValue && this.inputValue !== '' && this.isNumeric(this.inputValue)) {
        this.$emit('onChange', {
          id: this.id,
          value: this.inputValue,
          unit: this.unitSelected,
        });
      }
    },
    onChange(event) {
      const value = event.target.value;
      this.validateInput(value, event);
    },
    triggerFocus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    focus() {
      this.$emit('focus', {
        id: this.id,
        value: this.inputValue,
        unit: this.unitSelected,
      });
    },
    blur(event) {
      if (event.target.value === '') {
        this.inputValue = 0;
        this.inValidValue = false;
        this.inputClass = '';
      }
      if (this.inValidValue) {
        this.inputValue = this.oldInputValue;
        event.target.value = this.oldInputValue;
        this.inValidValue = false;
        this.inputClass = '';
      } else {
        const newValue = parseFloat(this.inputValue);
        if (newValue != this.value) {
          event.target.value = newValue;
          this.$emit('onChange', {
            id: this.id,
            value: newValue,
            unit: this.unitSelected,
          });
        }
      }
    },
    requiredValidate() {
      this.inputValue = parseFloat(this.inputValue);
      if (this.required && this.inputValue === '') {
        this.$emit('invalid', true);
        this.inputClass = 'error';
      } else {
        this.$emit('invalid', false);
        this.inputClass = '';
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';

.gf_input[type='number']::-webkit-inner-spin-button,
.gf_input[type='number']::-webkit-outer-spin-button {
  display: none;
}

.gf_input[type='number'] {
  -moz-appearance: textfield;
}

.gf_control-input {
  display: flex;

  .gf_input {
    background: $black;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: $text-dark-high;
    padding: 9px 6px;
    height: 32px;
    border: 1px solid $black3;
    box-sizing: border-box;
    border-radius: 3px 0 0 3px;
    transition: border 200ms;

    &:hover {
      border: 1px solid map-get($hybrid-shade-light, '500');

      &[disabled] {
        border: 1px solid $black3;
      }
    }

    &:focus {
      border: 1px solid map-get($primary-blue, '275');
    }

    &.error {
      border: 1px solid $accent-red-new;
    }
  }
}

.gf_selection-select {
  @include flex-center;
  background: $black3;
  border: 1px solid $black3;
  box-sizing: border-box;
  border-radius: 0 3px 3px 0;
  border-left: none;
  padding: 8px;

  .select--text {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: $text-dark-disabled;
  }
}
</style>
